/**
 * 검사표 동기화 — 화면이 들고 있는 상태(`ReportState`)와 서버의 표(`InspectionRecord`)를 잇는다.
 *
 * 둘의 모양을 일부러 맞추지 않았다. 서버는 다른 API 처럼 스네이크 표기로 주고받고, 화면은
 * 브라우저에 저장하던 시절의 모양을 그대로 쓴다 — 옛 초안(`report.ts` 의 localStorage)을
 * 서버로 옮길 때 같은 파서를 쓰기 위해서다.
 */
import type { InspectionInput, InspectionRecord } from '../api/client.js';
import { emptyState, type ReportState } from './report.js';

/** 서버의 표를 화면 상태로. 아무도 저장하지 않은 표면 검사일만 오늘로 채운 빈 표다. */
export function stateFromRecord(record: InspectionRecord, today: string): ReportState {
  if (record.revision === 0) return emptyState(today);
  const { header } = record;
  return {
    header: {
      docNo: header.doc_no,
      customer: header.customer,
      site: header.site,
      system: header.system,
      inspectedOn: header.inspected_on,
      inspector: header.inspector,
      witness: header.witness,
    },
    results: { ...record.results },
    notes: { ...record.notes },
    conclusion: record.conclusion,
  };
}

/** 화면 상태를 저장할 모양으로. `revision` 은 이 상태를 읽었을 때의 판번호다. */
export function inputFromState(state: ReportState, revision: number): InspectionInput {
  const { header } = state;
  const results: InspectionInput['results'] = {};
  for (const [id, verdict] of Object.entries(state.results)) {
    if (verdict) results[id] = verdict;
  }
  return {
    header: {
      doc_no: header.docNo,
      customer: header.customer,
      site: header.site,
      system: header.system,
      inspected_on: header.inspectedOn,
      inspector: header.inspector,
      witness: header.witness,
    },
    results,
    notes: Object.fromEntries(Object.entries(state.notes).filter(([, note]) => note !== '')),
    conclusion: state.conclusion,
    revision,
  };
}

/**
 * 누가 적은 것이 있는가 — 옛 초안을 서버로 옮길지 가를 때 쓴다.
 *
 * 검사일은 빼고 본다. 빈 표도 오늘 날짜가 채워져 있어서, 그것까지 세면 브라우저를 한 번
 * 열었을 뿐인 초안이 서버의 빈 표를 덮는다.
 */
export function hasContent(state: ReportState): boolean {
  return (
    Object.values(state.results).some(Boolean) ||
    Object.values(state.notes).some((note) => note.trim() !== '') ||
    state.conclusion.trim() !== '' ||
    Object.entries(state.header).some(([key, value]) => key !== 'inspectedOn' && value.trim() !== '')
  );
}

/** 저장한 사람 — `user:hyeok@…` 은 이메일만, 서비스 토큰은 그렇다고 밝힌다. */
export function savedByLabel(updatedBy: string): string {
  if (updatedBy.startsWith('user:')) return updatedBy.slice('user:'.length);
  if (updatedBy.startsWith('service:')) return `서비스 토큰 ${updatedBy.slice('service:'.length)}`;
  return updatedBy || '—';
}
